import React from "react";
import PropTypes from "prop-types";

// The pill recipe from Alyssa's V2 designs, expressed entirely in existing token utilities:
// Accent Blue fill, a 1px Accent Blue stroke (which .border-accent-blue-* sets for us) and Accent
// Blue text — sized a step below the body copy so the chips read as metadata rather than
// competing with it. `selected` inverts the same ladder rather than introducing a colour of its own.
const BASE_CLASSES = "qw-tag badge fw-semibold paragraph-xs rounded-pill d-inline-flex align-items-center border";
const RESTING_CLASSES = "text-accent-blue-80 bg-accent-blue-10 border-accent-blue-30";
const SELECTED_CLASSES = "text-white bg-accent-blue-80 border-accent-blue-80";

/**
 * Tag — the shared chip/pill used wherever a profile page shows hashtags.
 *
 * Presentation only: it does not fetch, does not know what a hashtag is, and does not decide
 * whether it is a link. Callers that have a destination pass `href` and get an `<a>`; callers
 * that do not get a `<span>`, never an anchor without a destination.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The chip's label
 * @param {string} [props.href] - Destination; omitted/null renders a plain span
 * @param {boolean} [props.selected] - Renders the inverted "on" treatment (e.g. an active search facet)
 * @param {Function} [props.onClick] - Click handler (click tracking, facet toggling). Rides the
 *   anchor, so it only takes effect alongside `href`; a chip with no destination is not a control.
 * @param {string} [props.className] - Extra classes appended to the chip (spacing is the caller's)
 */
function Tag({ children, href = null, selected = false, onClick = null, className = "" }) {
  const classes = `${BASE_CLASSES} ${selected ? SELECTED_CLASSES : RESTING_CLASSES} ${className}`.trim();

  if (href) {
    return (
      <a href={href} className={classes} onClick={onClick}>
        {children}
      </a>
    );
  }

  return <span className={classes}>{children}</span>;
}

Tag.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

export default Tag;
